import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { primaryCarrierShipment } from "@redotech/redo-fulfillment-group-common/shipment-summary";
import { useTranslation } from "@redotech/redo-i18n";
import ZapFastIcon from "@redotech/redo-icons/zap-fast_filled.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { getEffectiveShipmentSummary } from "../utils/effective-shipment-summary";

export const PreboughtLabelIcon = memo(function PreboughtLabelIcon({
  fulfillmentGroups,
  selectedRateKey,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[] | undefined;
  selectedRateKey: string;
}) {
  const team = useRequiredContext(TeamContext);
  const outboundLabelsClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const { t } = useTranslation();
  const [group] = fulfillmentGroups ?? [];
  const purchased = !!primaryCarrierShipment(getEffectiveShipmentSummary(group))
    ?.purchased;
  const groupId =
    fulfillmentGroups?.length === 1 &&
    group &&
    team.featureFlags?.[OmsFlagKey.LABEL_PREBUY]
      ? group._id
      : undefined;
  const preboughtLoad = useLoad(
    async (signal: AbortSignal) => {
      if (!groupId) {
        return false;
      }
      const { prebought } = await outboundLabelsClient.getLabelPrebought(
        { fulfillmentGroupId: groupId },
        { signal },
      );
      return prebought;
    },
    [outboundLabelsClient, groupId, selectedRateKey, purchased],
  );
  if (!groupId || preboughtLoad.value !== true) {
    return null;
  }
  return (
    <Tooltip
      leftAlign
      subtitle={
        purchased ? undefined : (
          <Text fontSize="xs" textColor="white">
            {t(
              "We prepared this label early. If anything changes, we'll update it automatically.",
            )}
          </Text>
        )
      }
      title={
        <Text fontSize="xs" fontWeight="semibold" textColor="white">
          {purchased
            ? t("Prepared early for instant printing")
            : t("Ready to print instantly")}
        </Text>
      }
    >
      <Flex flexDirection="column" justify="center">
        <RedoIcon color="orange" Icon={ZapFastIcon} />
      </Flex>
    </Tooltip>
  );
});
